// CB:BEGIN admin-priority3-audit-log-viewer-component
/**
 * Audit Log Viewer Component
 * Displays comprehensive audit trail with filtering and search
 */
import React, { useState, useEffect } from 'react';
import { useApi } from '../../lib/useApi';

interface AuditLog {
  id: string;
  actorUserId: string | null;
  entityType: string;
  entityId: string;
  action: string;
  payload: any;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  actor?: {
    id: string;
    email: string;
    role: string;
  } | null;
}

interface AuditLogStats {
  total: number;
  byAction: Array<{ action: string; count: number }>;
  byEntityType: Array<{ entityType: string; count: number }>;
}

export const AuditLogViewer: React.FC = () => {
  const api = useApi();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Filters
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '50');
      if (action) params.append('action', action);
      if (entityType) params.append('entityType', entityType);
      if (entityId) params.append('entityId', entityId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const data = await api.get(`/api/admin/audit-logs?${params.toString()}`);
      setLogs(data.logs);
      setTotal(data.pagination.total);
      setTotalPages(data.pagination.pages);
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to fetch audit logs'}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const data = await api.get(`/api/admin/audit-logs/stats?${params.toString()}`);
      setStats(data);
    } catch (e: any) {
      console.error('Failed to fetch stats:', e);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, action, entityType, entityId, startDate, endDate]);

  const handleReset = () => {
    setAction('');
    setEntityType('');
    setEntityId('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getActionColor = (action: string) => {
    if (action.includes('delete') || action.includes('suspend') || action.includes('ban')) return '#ef4444';
    if (action.includes('create') || action.includes('add')) return '#10b981';
    if (action.includes('update') || action.includes('edit')) return '#f59e0b';
    return '#6b7280';
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '700' }}>
        Audit Log Viewer
      </h2>

      {/* Statistics Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd' }}>
            <div style={{ fontSize: '14px', color: '#1e40af', marginBottom: '4px' }}>Total Logs</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e3a8a' }}>{stats.total.toLocaleString()}</div>
          </div>
          <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
            <div style={{ fontSize: '14px', color: '#166534', marginBottom: '4px' }}>Unique Actions</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#15803d' }}>{stats.byAction.length}</div>
          </div>
          <div style={{ padding: '16px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24' }}>
            <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '4px' }}>Entity Types</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#78350f' }}>{stats.byEntityType.length}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        padding: '20px',
        background: '#f9fafb',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '1px solid #e5e7eb'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Filters</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Action
            </label>
            <input
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g., admin_suspend_contractor"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Entity Type
            </label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Types</option>
              <option value="User">User</option>
              <option value="Contractor">Contractor</option>
              <option value="Project">Project</option>
              <option value="Escrow">Escrow</option>
              <option value="Dispute">Dispute</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Entity ID
            </label>
            <input
              type="text"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="Entity ID"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={handleReset}
              style={{
                padding: '8px 16px',
                background: '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
            <div style={{ fontSize: '16px' }}>Loading audit logs...</div>
          </div>
        ) : logs.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Timestamp</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Actor</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Action</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Entity</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>IP Address</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {formatDate(log.createdAt)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {log.actor ? (
                          <div>
                            <div style={{ color: '#111827', fontWeight: '500' }}>{log.actor.email}</div>
                            <div style={{ color: '#9ca3af', fontSize: '12px' }}>{log.actor.role}</div>
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>System</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: `${getActionColor(log.action)}20`,
                          color: getActionColor(log.action),
                          fontSize: '12px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {log.action}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        <div style={{ color: '#111827', fontWeight: '500' }}>{log.entityType}</div>
                        <div style={{ color: '#9ca3af', fontSize: '11px', fontFamily: 'monospace' }}>
                          {log.entityId.substring(0, 8)}...
                        </div>
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280', fontFamily: 'monospace' }}>
                        {log.ipAddress || '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => setSelectedLog(log)}
                          style={{
                            padding: '4px 12px',
                            background: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              padding: '16px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Showing {(page - 1) * 50 + 1} - {Math.min(page * 50, total)} of {total.toLocaleString()} logs
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  style={{
                    padding: '8px 16px',
                    background: page === 1 ? '#e5e7eb' : '#fff',
                    color: page === 1 ? '#9ca3af' : '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  style={{
                    padding: '8px 16px',
                    background: page === totalPages ? '#e5e7eb' : '#fff',
                    color: page === totalPages ? '#9ca3af' : '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📋</div>
            <div style={{ fontSize: '16px', fontWeight: '500', color: '#111827', marginBottom: '8px' }}>
              No audit logs found
            </div>
            <div style={{ fontSize: '14px' }}>
              Try adjusting your filters to see more results
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Audit Log Details
            </h3>

            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>ID</div>
                <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#111827' }}>{selectedLog.id}</div>
              </div>

              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Timestamp</div>
                <div style={{ fontSize: '14px', color: '#111827' }}>{formatDate(selectedLog.createdAt)}</div>
              </div>

              {selectedLog.actor && (
                <div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Actor</div>
                  <div style={{ fontSize: '14px', color: '#111827' }}>
                    {selectedLog.actor.email} ({selectedLog.actor.role})
                  </div>
                </div>
              )}

              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Action</div>
                <div style={{ fontSize: '14px', color: '#111827', fontWeight: '600' }}>{selectedLog.action}</div>
              </div>

              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Entity</div>
                <div style={{ fontSize: '14px', color: '#111827' }}>
                  {selectedLog.entityType}: {selectedLog.entityId}
                </div>
              </div>

              {selectedLog.ipAddress && (
                <div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>IP Address</div>
                  <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#111827' }}>{selectedLog.ipAddress}</div>
                </div>
              )}

              {selectedLog.userAgent && (
                <div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>User Agent</div>
                  <div style={{ fontSize: '13px', color: '#111827', wordBreak: 'break-word' }}>{selectedLog.userAgent}</div>
                </div>
              )}

              {selectedLog.payload && (
                <div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Payload</div>
                  <pre style={{
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px'
                  }}>
                    {JSON.stringify(selectedLog.payload, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={() => setSelectedLog(null)}
                style={{
                  padding: '8px 16px',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// CB:END admin-priority3-audit-log-viewer-component
